"use client";

import { useEffect, useState } from "react";
import { Download, FileArchive, FileText, Languages, Loader2, RefreshCw, ScrollText, ShieldCheck } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useNotify } from "@/components/toaster/app-toaster";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { locales } from "@/i18n/locales";
import { storedFileIdOf } from "./signature-badge";

const fileHref = (id: string) => `/api/documents/explorer/file/${id}`;

type Sheet = { id: string | null; evidenceId: string | null; evidenceExact: boolean; exact: boolean; canBuild: boolean; canRegenerate: boolean };

/** Asks for the information sheet of kept file `fileId` in the viewer's language: `run(false)` builds it when missing, `run(true)` deletes this copy and builds it again (needs write). Gives the sheet's file id, or null after telling the person it failed. */
function useSheetRequest(fileId: string | null) {
  const locale = useLocale();
  const t = useTranslations("pdfPreview.sheet");
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const run = async (regenerate: boolean, language = locale, evidence = false): Promise<string | null> => {
    if (!fileId) return null;
    setBusy(true);
    const res = await fetch(`${fileHref(fileId)}/sheet?locale=${language}${regenerate ? "&regenerate=1" : ""}${evidence ? "&evidence=1" : ""}`, { method: "POST", credentials: "same-origin" }).catch(() => null);
    const body = res?.ok ? ((await res.json()) as { id?: string }) : null;
    setBusy(false);
    if (!body?.id) notify.error(t("regenerateFailed"));
    else if (regenerate) notify.success(t("regenerated"));
    return body?.id ?? null;
  };
  return { busy, run, locale };
}

/** The information sheet of the kept file behind `href` in the viewer's language: what the server knows (a copy to show, whether one can be built, whether the person may regenerate it) and `build` / `regenerate`, which make the copy and show it. A legacy four-language copy is shown until the right one is built. */
export function useInfoSheet(href: string, active = true) {
  const id = storedFileIdOf(href);
  const { busy, run, locale } = useSheetRequest(id);
  const [sheet, setSheet] = useState<Sheet | null>(null);
  useEffect(() => {
    if (!id || !active) return;
    let live = true;
    void fetch(`${fileHref(id)}/sheet?locale=${locale}`, { credentials: "same-origin" })
      .then((r) => (r.ok ? (r.json() as Promise<Sheet>) : null))
      .then((b) => { if (live) setSheet(b); })
      .catch(() => undefined);
    return () => { live = false; };
  }, [id, active, locale]);
  const make = async (regenerate: boolean, language?: string) => { const made = await run(regenerate, language); if (made) setSheet((p) => (p ? { ...p, id: made, exact: true } : p)); };
  const makeEvidence = async (regenerate: boolean, language?: string) => { const made = await run(regenerate, language, true); if (made) setSheet((p) => (p ? { ...p, evidenceId: made, evidenceExact: true } : p)); };
  return { available: !!sheet && (sheet.canBuild || !!sheet.id), id: sheet?.id ?? null, evidenceId: sheet?.evidenceId ?? null, evidenceExact: sheet?.evidenceExact ?? false, buildEvidence: () => makeEvidence(false), regenerateEvidence: (language?: string) => makeEvidence(true, language), exact: sheet?.exact ?? false, canRegenerate: sheet?.canRegenerate ?? false, busy, build: () => make(false), regenerate: (language?: string) => make(true, language) };
}

const LANGUAGE_NAMES: Record<string, string> = { en: "English", fr: "Français", es: "Español", ar: "العربية" };

/** "Regenerate information sheet": the button remakes the sheet in the viewer's language; its menu remakes it once in another language (the new sheet replaces the stored one of that language). */
export function RegenerateSheetButton({ onClick, busy, iconOnly = true }: { onClick: (language?: string) => void; busy: boolean; iconOnly?: boolean }) {
  const t = useTranslations("pdfPreview.sheet");
  const current = useLocale();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger render={<Button type="button" variant={iconOnly ? "ghost" : "outline"} size={iconOnly ? "icon" : "default"} disabled={busy} title={t("regenerate")} aria-label={t("regenerate")} />}>{busy ? <Loader2 className="animate-spin" aria-hidden /> : <RefreshCw aria-hidden />}</DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {locales.map((l) => <DropdownMenuItem key={l} onClick={() => onClick(l === current ? undefined : l)}><Languages aria-hidden />{LANGUAGE_NAMES[l] ?? l}</DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The sheet of the file `fileId` for a Files row: the same Regenerate button, for someone who may write. */
export function RowRegenerateSheet({ fileId }: { fileId: string }) {
  const { busy, run } = useSheetRequest(fileId);
  return <RegenerateSheetButton busy={busy} onClick={(language) => void run(true, language)} />;
}

/** The one control of the viewer that switches between the signed document and its information sheet. */
export type SheetView = "document" | "sheet" | "evidence";

export function InfoSheetSwitch({ value, onChange, evidence = false }: { value: SheetView; onChange: (next: SheetView) => void; evidence?: boolean }) {
  const t = useTranslations("pdfPreview.sheet");
  return (
    <ToggleGroup variant="outline" spacing={0} value={[value]} aria-label={t("switch")} onValueChange={(next) => { if (next[0] === "document" || next[0] === "sheet" || next[0] === "evidence") onChange(next[0]); }}>
      <ToggleGroupItem value="document"><FileText aria-hidden />{t("document")}</ToggleGroupItem>
      <ToggleGroupItem value="sheet"><ScrollText aria-hidden />{t("sheet")}</ToggleGroupItem>
      {evidence && <ToggleGroupItem value="evidence"><ShieldCheck aria-hidden />{t("evidence")}</ToggleGroupItem>}
    </ToggleGroup>
  );
}

/** Starts a download without leaving the page. */
function saveFrom(href: string) {
  const link = document.createElement("a");
  link.href = href;
  link.download = "";
  document.body.append(link);
  link.click();
  link.remove();
}

/** "Download both": a menu with the file and its sheet (in the viewer's language, made when missing) as two downloads one after the other, or as one zip (the fallback when the browser blocks the second download). The file's bytes are the stored ones either way. */
export function DownloadBothMenu({ fileId, iconOnly = false }: { fileId: string; iconOnly?: boolean }) {
  const t = useTranslations("pdfPreview.sheet");
  const { run, locale } = useSheetRequest(fileId);
  const separate = async () => { const sheetId = await run(false); if (!sheetId) return; saveFrom(fileHref(fileId)); setTimeout(() => saveFrom(fileHref(sheetId)), 400); };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger render={<Button type="button" variant="outline" size={iconOnly ? "icon" : "default"} title={t("downloadBoth")} aria-label={t("downloadBoth")} />}><Download aria-hidden />{!iconOnly && t("downloadBoth")}</DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => void separate()}><Download aria-hidden />{t("separate")}</DropdownMenuItem>
        <DropdownMenuItem onClick={() => saveFrom(`${fileHref(fileId)}/both?locale=${locale}`)}><FileArchive aria-hidden />{t("zip")}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
